import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './MusicVideoPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import kisanMan from '../assets/kisanman.mp4';

const MusicVideoPage = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().then(() => {
          setIsPlaying(true);
          videoRef.current.muted = false;
          setIsMuted(false);
        }).catch(err => {
          console.log("Play failed: ", err);
        });
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeToggle = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Ensure initial load duration setting if metadata fires early
  useEffect(() => {
    if (videoRef.current && videoRef.current.duration) {
      setDuration(videoRef.current.duration);
    }
  }, []);

  return (
    <div className="music-video-page">
      <Header />
      
      <main className="music-video-main">
        <div className="container">
          <div className="music-video-header">
            <div className="music-note-header">
              <span className="line"></span>
              <span className="note-icon">🎵</span>
              <span className="line"></span>
            </div>
            <h1 className="music-video-title">{t('music_video.title', 'Listen to the Sound of Our Brand')}</h1>
          </div>

          <div className="music-player-wrapper">
            <div className="video-card">
              <div className={`video-card-led ${isPlaying ? 'playing' : 'paused'}`}></div>
              <video
                ref={videoRef}
                src={kisanMan}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onClick={handlePlayPause}
                className="player-video"
                muted={isMuted}
                playsInline
              />
              {!isPlaying && (
                <button className="center-play-overlay" onClick={handlePlayPause} aria-label="Play video">
                  <div className="play-triangle"></div>
                </button>
              )}
            </div>

            <div className="player-controls-bar">
              <button className="control-btn play-btn" onClick={handlePlayPause} aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
              <span className="time-display">{formatTime(currentTime)}</span>
              <input
                type="range"
                min="0"
                max={duration || 33}
                step="0.1"
                value={currentTime}
                onChange={handleSeek}
                className="progress-slider"
                style={{
                  background: `linear-gradient(to right, #4BA83D 0%, #4BA83D ${(currentTime / (duration || 33)) * 100}%, #e0e0e0 ${(currentTime / (duration || 33)) * 100}%, #e0e0e0 100%)`
                }}
              />
              <span className="time-display">{formatTime(duration || 33)}</span>
              <button className="control-btn volume-btn" onClick={handleVolumeToggle} aria-label={isMuted ? "Unmute" : "Mute"}>
                {isMuted ? (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.21.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.01 14.91 20.5 13.51 20.5 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.03a11.94 11.94 0 0 0 3.69-1.92L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
              <button className="control-btn menu-btn" aria-label="More options">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <circle cx="12" cy="5" r="2"/>
                  <circle cx="12" cy="12" r="2"/>
                  <circle cx="12" cy="19" r="2"/>
                </svg>
              </button>
            </div>

            <div className="duration-label">
              {t('music_video.duration', 'Duration')}: {formatTime(duration || 33)} {t('music_video.seconds', 'Seconds')}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MusicVideoPage;


